/* =========================================================================
   Home Store Online – product page behaviour for the hs-* sections
   Ported from the prototype (storefront/assets/theme.js, "product page").
   - gallery: thumbnails, arrows, swipe, keyboard, hover loupe
   - size tiles: price, compare-at, pay-in-3, stock line, SKU, sticky bar, ?variant=
   - add to basket with Shopify's Ajax Cart API; Horizon's cart drawer and
     cart count update through the standard cart event (@shopify/events)
   - sticky add-to-basket bar, size guide dialog + room planner
   - delivery estimate, recommendations, recently viewed, rail arrows
   ========================================================================= */

let CartLinesUpdateEvent = null;
try {
  ({ CartLinesUpdateEvent } = await import('@shopify/events'));
} catch (e) {
  /* Horizon not present: fall back to going to the cart page after adding. */
}

const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
const routes = (window.Theme && window.Theme.routes) || {};
const CART_ADD = routes.cart_add_url || '/cart/add.js';
const CART_URL = routes.cart_url || '/cart';
const store = {
  get(k, d) { try { const v = JSON.parse(localStorage.getItem(k)); return v == null ? d : v; } catch (e) { return d; } },
  set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* private mode */ } }
};

/* ---------------- size guide + room planner ---------------- */
const RUG_SIZES = [[80, 150], [120, 170], [150, 200], [160, 230], [200, 290], [240, 340]];
const RUNNERS = [[60, 200], [60, 230], [66, 230], [60, 240], [80, 300]];
function recommend(room, W, L) {
  const w = Math.min(W, L) * 100, l = Math.max(W, L) * 100;
  if (room === 'hall') {
    const fit = RUNNERS.filter(r => r[0] <= w - 20 && r[1] <= l - 40).sort((x, y) => y[1] - x[1] || y[0] - x[0]);
    return { size: fit[0] || RUNNERS[0], note: fit[0] ? 'it leaves floor showing at both ends and sides' : 'your hallway is compact, so choose the shortest runner' };
  }
  const clear = 45, ideal = { living: [160, 230], bed: [160, 230], kingbed: [200, 290], dining: [200, 290] }[room];
  const fit = RUG_SIZES.filter(r => r[0] <= w - 2 * clear && r[1] <= l - 2 * clear);
  let pick = fit.filter(r => r[0] * r[1] <= ideal[0] * ideal[1] * 1.6).pop() || fit.pop() || RUG_SIZES[0];
  if (room === 'dining' && pick[0] < 200 && fit.length) pick = fit[fit.length - 1];
  const note = { living: 'the front legs of the sofa sit on the rug with floor showing all round', bed: 'the rug sits under the lower two-thirds of a double bed', kingbed: 'the rug sits under the lower two-thirds of a king-size bed', dining: 'chairs stay on the rug when pulled out' }[room];
  return { size: pick, note };
}
function planSVG(room, W, L, size) {
  const Wd = Math.max(W, L), Hd = Math.min(W, L), s = 380 / Wd / 100, rw = size[1] * s, rh = size[0] * s, vw = 400, vh = Math.max(120, Hd * 100 * s + 20);
  const rx = (vw - rw) / 2, ry = (vh - rh) / 2;
  let furn = '';
  if (room === 'living') furn = `<rect x="${vw / 2 - 110 * s}" y="${ry - 30 * s}" width="${220 * s}" height="${90 * s}" rx="4" fill="#0F1A33"/><rect x="${vw / 2 - 55 * s}" y="${vh / 2 - 25 * s}" width="${110 * s}" height="${55 * s}" rx="3" fill="none" stroke="#0F1A33" stroke-width="1.5"/>`;
  if (room === 'bed' || room === 'kingbed') { const bw = (room === 'bed' ? 135 : 150) * s; furn = `<rect x="${rx - 10}" y="${vh / 2 - bw / 2}" width="${200 * s}" height="${bw}" rx="4" fill="#0F1A33" opacity=".9"/>`; }
  if (room === 'dining') furn = `<rect x="${vw / 2 - 90 * s}" y="${vh / 2 - 45 * s}" width="${180 * s}" height="${90 * s}" rx="3" fill="#0F1A33"/>`;
  return `<svg viewBox="0 0 ${vw} ${vh}" role="img" aria-label="Floor plan with suggested rug"><rect x="10" y="10" width="${vw - 20}" height="${vh - 20}" fill="#FBFAF6" stroke="#0F1A33" stroke-width="2"/>` +
    `<rect x="${rx}" y="${ry}" width="${rw}" height="${rh}" fill="#D2352B" opacity=".8" rx="2"/>${furn}` +
    `<text x="20" y="${vh - 18}" font-family="Figtree, Arial" font-size="12" fill="#5B6273">Room ${W.toFixed(1)} × ${L.toFixed(1)} m · rug ${size[0]} × ${size[1]} cm</text></svg>`;
}
function initSizeGuide(root, allUrl) {
  const dlg = $('[data-hs-dialog="sizeguide"]', root);
  if (!dlg || dlg.dataset.bound) return;
  dlg.dataset.bound = '1';
  const f = $('[data-planner]', dlg), out = $('[data-plan-out]', dlg);
  const render = () => {
    const room = f.room.value, W = parseFloat(f.w.value) || 3, L = parseFloat(f.l.value) || 4;
    const r = recommend(room, W, L), sz = r.size, label = `${sz[0]} x ${sz[1]} cm`;
    out.innerHTML = `<p class="eyebrow">Our suggestion</p><p class="h2" style="font-size:1.8rem;margin:6px 0 4px">${label}</p><p class="muted" style="margin:0 0 14px">So ${r.note}.</p>${planSVG(room, W, L, sz)}` +
      `<p style="margin-top:14px;display:flex;gap:10px;flex-wrap:wrap"><a class="btn" href="${allUrl}?filter.v.option.size=${encodeURIComponent(label)}">Shop ${label} rugs</a></p>`;
  };
  f.addEventListener('input', render); f.addEventListener('submit', e => e.preventDefault()); render();
  $$('[data-tab]', dlg).forEach(t => t.addEventListener('click', () => {
    $$('[data-tab]', dlg).forEach(x => x.setAttribute('aria-selected', x === t ? 'true' : 'false'));
    $$('[data-panel]', dlg).forEach(p => { p.hidden = p.dataset.panel !== t.dataset.tab; });
  }));
  $$('[data-hs-close]', dlg).forEach(b => b.addEventListener('click', () => dlg.close()));
  dlg.addEventListener('click', e => { if (e.target === dlg) dlg.close(); });
}
/* Any [data-hs-open="sizeguide"] on the page opens the dialog of the main product section. */
document.addEventListener('click', e => {
  const b = e.target.closest('[data-hs-open="sizeguide"]');
  if (!b) return;
  const dlg = $('[data-hs-dialog="sizeguide"]');
  if (dlg && typeof dlg.showModal === 'function') { e.preventDefault(); dlg.showModal(); }
});

/* ---------------- delivery estimate ---------------- */
function workingDaysFrom(d, n) { const x = new Date(d); let k = 0; while (k < n) { x.setDate(x.getDate() + 1); const wd = x.getDay(); if (wd !== 0 && wd !== 6) k++; } return x; }
const fmtD = d => d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
function eta(P) {
  const now = new Date(), late = now.getHours() >= P.cutoffHour ? 1 : 0;
  const cut = P.cutoffHour > 12 ? `${P.cutoffHour - 12}pm` : `${P.cutoffHour}am`;
  return `Order ${late ? 'now' : `by ${cut} today`} for delivery between <b>${fmtD(workingDaysFrom(now, P.minDays + late))}</b> and <b>${fmtD(workingDaysFrom(now, P.maxDays + late))}</b>.`;
}

/* ---------------- add to basket ---------------- */
async function addToCart(items, sourceEl, productId) {
  const sectionIds = $$('cart-items-component').map(el => el.dataset.sectionId).filter(Boolean);
  const qty = items.reduce((n, i) => n + i.quantity, 0);
  let deferred = null;
  if (CartLinesUpdateEvent) {
    deferred = CartLinesUpdateEvent.createPromise();
    sourceEl.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add', context: 'product',
      lines: items.map(i => ({ merchandiseId: String(i.id), quantity: i.quantity })),
      promise: deferred.promise
    }));
  }
  const body = { items };
  if (sectionIds.length) { body.sections = sectionIds.join(','); body.sections_url = location.pathname; }
  try {
    const res = await fetch(CART_ADD, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify(body) });
    const json = await res.json();
    const cart = await fetch(`${CART_URL}.js`, { headers: { Accept: 'application/json' } }).then(r => r.json());
    const failed = !!json.status;
    if (deferred) {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'hs-main-product', sourceId: String(productId), itemCount: qty, productId: String(productId), sections: json.sections, didError: failed }
      });
    } else if (!failed) {
      location.href = CART_URL;
    }
    if (failed) throw new Error(json.description || json.message || 'Could not add to basket');
    return true;
  } catch (err) {
    if (deferred) { try { deferred.reject(err); } catch (e) { /* already settled */ } }
    throw err;
  }
}

/* ---------------- main product section ---------------- */
function initProduct(root) {
  if (root.dataset.hsReady) return;
  root.dataset.hsReady = '1';
  const P = JSON.parse($('[data-hs-product-json]', root).textContent);
  const form = $('form.hs-form', root);
  const idInput = $('[data-variant-input]', root);
  const opts = $$('.size-opt', root);
  let cur = Math.max(0, P.variants.findIndex(v => String(v.id) === String(idInput && idInput.value)));

  function stockText(v) {
    if (!v.available) return ['out', `Sold out in ${v.label}.`];
    if (v.tracked && v.qty > 0 && v.qty <= P.lowStock) return ['low', `Only ${v.qty} left in ${v.label}. Ready to dispatch`];
    return ['', `In stock in ${v.label}. Ready to dispatch`];
  }
  function underlayFor(v) {
    if (!P.underlay.length) return null;
    const norm = t => String(t || '').toLowerCase().replace(/\s+/g, '');
    return P.underlay.find(u => norm(u.label) === norm(v.size)) || null;
  }
  /* products with several options (e.g. Size + Colour): one button group per option */
  const optBtns = $$('[data-opt]', root);
  const findIndex = sel => P.variants.findIndex(v => v.options.every((o, k) => o === sel[k]));
  function syncOptions(v) {
    if (!optBtns.length) return;
    optBtns.forEach(b => {
      const k = +b.dataset.opt, val = b.dataset.value;
      b.setAttribute('aria-checked', v.options[k] === val ? 'true' : 'false');
      const trial = v.options.slice(); trial[k] = val;
      const j = findIndex(trial);
      b.dataset.oos = (j < 0 || !P.variants[j].available) ? 'true' : 'false';
    });
    v.options.forEach((o, k) => { const el = $(`[data-opt-label="${k}"]`, root); if (el) el.textContent = o; });
  }
  optBtns.forEach(b => b.addEventListener('click', () => {
    const k = +b.dataset.opt, sel = P.variants[cur].options.slice();
    sel[k] = b.dataset.value;
    let j = findIndex(sel);
    if (j < 0) j = P.variants.findIndex(v => v.options[k] === sel[k] && v.available);
    if (j < 0) j = P.variants.findIndex(v => v.options[k] === sel[k]);
    if (j >= 0) select(j, true, true);
  }));
  function select(i, push = true, showMedia = false) {
    cur = i; const v = P.variants[i];
    if (idInput) idInput.value = v.id;
    opts.forEach(o => o.setAttribute('aria-checked', +o.dataset.v === i ? 'true' : 'false'));
    const set = (sel, html) => { const el = $(sel, root); if (el) el.innerHTML = html; };
    set('[data-price]', v.price); set('[data-bnpl]', v.bnpl); set('[data-size-label]', v.size);
    syncOptions(v);
    const dc = $('[data-dynamic-checkout]', root); if (dc) dc.hidden = !v.available;
    if (showMedia && v.media >= 0 && typeof show === 'function') show(v.media);
    const cmp = $('[data-compare]', root); if (cmp) { cmp.hidden = !v.compare; cmp.textContent = v.compare || ''; }
    $$('[data-hs-sku]').forEach(el => { el.textContent = v.sku || ''; });
    const [cls, txt] = stockText(v);
    const line = $('[data-stock-line]', root); if (line) line.className = 'stock-line' + (cls ? ' ' + cls : '');
    set('[data-stock-text]', txt);
    const atc = $('[data-atc]', root);
    if (atc) { atc.disabled = !v.available; atc.innerHTML = v.available ? `Add to basket · <span data-atc-price>${v.price}</span>` : 'Sold out'; }
    const runner = $('[data-addon-runner]', root); if (runner) runner.hidden = P.runnerIndex === i;
    const ulRow = $('[data-addon-underlay]', root);
    if (ulRow) { const u = underlayFor(v); ulRow.hidden = !u; if (u) { set('[data-underlay-size]', v.size); set('[data-underlay-price]', '+' + u.price); } }
    set('[data-sticky-meta]', `${v.label} · ${v.price}`);
    const ss = $('[data-sticky-select]', root); if (ss) ss.value = String(i);
    const sa = $('[data-sticky-add]', root); if (sa) { sa.disabled = !v.available; sa.textContent = v.available ? 'Add to basket' : 'Sold out'; }
    const e = $('[data-eta]', root); if (e) e.innerHTML = P.showEta ? eta(P) : '';
    if (push && P.variants.length > 1) { try { const u = new URL(location.href); u.searchParams.set('variant', v.id); history.replaceState(history.state, '', u); } catch (err) { /* ignore */ } }
  }
  opts.forEach(o => o.addEventListener('click', () => select(+o.dataset.v)));
  const ss = $('[data-sticky-select]', root); if (ss) ss.addEventListener('change', e => select(+e.target.value, true, true));

  const qIn = $('[data-qty-input]', root);
  $$('[data-qty]', root).forEach(b => b.addEventListener('click', () => { qIn.value = Math.max(1, (parseInt(qIn.value, 10) || 1) + +b.dataset.qty); }));

  const errEl = $('[data-atc-error]', root);
  async function addCurrent(btn) {
    const v = P.variants[cur]; if (!v.available) return;
    const qty = Math.max(1, parseInt(qIn && qIn.value, 10) || 1);
    const items = [{ id: v.id, quantity: qty }];
    const ra = $('[data-addon="runner"]', root);
    if (ra && ra.checked && P.runnerIndex >= 0 && P.runnerIndex !== cur) items.push({ id: P.variants[P.runnerIndex].id, quantity: 1 });
    const ul = $('[data-addon="underlay"]', root), u = underlayFor(v);
    if (ul && ul.checked && u && u.available) items.push({ id: u.id, quantity: qty });
    const label = btn.innerHTML; btn.disabled = true; btn.textContent = 'Adding…';
    if (errEl) errEl.hidden = true;
    try {
      await addToCart(items, btn, P.id);
      if (ra) ra.checked = false; if (ul) ul.checked = false;
      btn.textContent = 'Added';
      setTimeout(() => { btn.innerHTML = label; btn.disabled = false; }, 1400);
    } catch (err) {
      btn.innerHTML = label; btn.disabled = false;
      if (errEl) { errEl.textContent = err.message; errEl.hidden = false; }
    }
  }
  if (form) form.addEventListener('submit', e => { e.preventDefault(); addCurrent($('[data-atc]', root)); });
  const sa = $('[data-sticky-add]', root); if (sa) sa.addEventListener('click', () => addCurrent(sa));

  /* gallery */
  const mainBox = $('[data-main]', root), main = mainBox && $('img', mainBox), thumbs = $$('[data-thumb]', root);
  const count = $('[data-count]', root), prog = $('[data-progress]', root), loupe = $('[data-loupe]', root);
  let gi = 0;
  function show(i) {
    if (!P.media.length || !main) return;
    gi = (i + P.media.length) % P.media.length; const g = P.media[gi];
    main.removeAttribute('srcset'); main.removeAttribute('sizes');
    main.src = g.src; main.alt = g.alt;
    thumbs.forEach(t => t.setAttribute('aria-current', +t.dataset.thumb === gi ? 'true' : 'false'));
    if (count) count.textContent = `${gi + 1} / ${P.media.length}`;
    if (prog) prog.style.width = ((gi + 1) / P.media.length * 100) + '%';
  }
  thumbs.forEach(t => t.addEventListener('click', () => show(+t.dataset.thumb)));
  const pv = $('[data-prev]', root), nx = $('[data-next]', root);
  if (pv) pv.addEventListener('click', () => show(gi - 1));
  if (nx) nx.addEventListener('click', () => show(gi + 1));
  if (mainBox) {
    let tx = null;
    mainBox.addEventListener('touchstart', e => { tx = e.touches[0].clientX; }, { passive: true });
    mainBox.addEventListener('touchend', e => { if (tx == null) return; const dx = e.changedTouches[0].clientX - tx; if (Math.abs(dx) > 40) show(gi + (dx < 0 ? 1 : -1)); tx = null; });
    mainBox.addEventListener('keydown', e => { if (e.key === 'ArrowRight') show(gi + 1); if (e.key === 'ArrowLeft') show(gi - 1); });
    if (loupe && main && matchMedia('(hover:hover) and (min-width:900px)').matches) {
      mainBox.addEventListener('mousemove', e => {
        if (e.target.closest('button') || !P.media[gi]) { loupe.style.display = 'none'; return; }
        const r = mainBox.getBoundingClientRect(), x = e.clientX - r.left, y = e.clientY - r.top;
        loupe.style.display = 'block'; loupe.style.left = (x - 95) + 'px'; loupe.style.top = (y - 95) + 'px';
        loupe.style.backgroundImage = `url("${P.media[gi].zoom}")`;
        const nw = main.naturalWidth || 1, nh = main.naturalHeight || 1, sc = Math.min(r.width / nw, r.height / nh), dw = nw * sc, dh = nh * sc, ox = (r.width - dw) / 2, oy = (r.height - dh) / 2, z = 2.6;
        loupe.style.backgroundSize = `${dw * z}px ${dh * z}px`;
        loupe.style.backgroundPosition = `${-((x - ox) * z - 95)}px ${-((y - oy) * z - 95)}px`;
      });
      mainBox.addEventListener('mouseleave', () => { loupe.style.display = 'none'; });
    }
  }

  /* sticky add-to-basket bar */
  const sticky = $('[data-sticky-atc]', root), atcBtn = $('[data-atc]', root);
  if (sticky && atcBtn && 'IntersectionObserver' in window) {
    new IntersectionObserver(([en]) => {
      const vis = !en.isIntersecting && en.boundingClientRect.top < 0;
      sticky.classList.toggle('is-visible', vis); sticky.setAttribute('aria-hidden', vis ? 'false' : 'true');
    }).observe(atcBtn);
  }

  initSizeGuide(root, P.allProductsUrl);

  /* remember this rug for "Recently viewed" */
  const rv = store.get('hs_recent', []).filter(h => h !== P.handle);
  store.set('hs_recent', [P.handle].concat(rv).slice(0, 12));

  select(cur, false);
}

/* ---------------- recommendations (Section Rendering) ---------------- */
async function initRecs(el) {
  if (el.dataset.hsReady || el.querySelector('[data-hs-recs-loaded]')) return;
  el.dataset.hsReady = '1';
  try {
    const html = await fetch(el.dataset.hsRecsUrl).then(r => r.text());
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const inner = doc.querySelector('[data-hs-recs-url]');
    if (inner && inner.querySelector('[data-hs-recs-loaded]')) { el.innerHTML = inner.innerHTML; initRails(el); }
  } catch (e) { /* leave the section empty */ }
}

/* ---------------- recently viewed ---------------- */
async function initRecent(wrap) {
  if (wrap.dataset.hsReady) return;
  wrap.dataset.hsReady = '1';
  const handles = store.get('hs_recent', []).filter(h => h !== wrap.dataset.current).slice(0, +wrap.dataset.limit || 8);
  if (!handles.length) return;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const cards = await Promise.all(handles.map(h => fetch(`${root}products/${encodeURIComponent(h)}?section_id=hs-product-card`)
    .then(r => (r.ok ? r.text() : '')).catch(() => '')));
  const html = cards.map(t => { const d = new DOMParser().parseFromString(t, 'text/html'); const c = d.querySelector('.hs-card'); return c ? c.outerHTML : ''; }).join('');
  if (!html) return;
  $('[data-hs-recent]', wrap).innerHTML = html;
  wrap.hidden = false;
  initRails(wrap);
}

/* ---------------- rail arrows ---------------- */
function initRails(scope) {
  $$('[data-hs-rail], .hs .rail', scope).forEach(r => {
    if (r.dataset.hsRail === 'ready' || r.parentElement.classList.contains('rail-wrap')) return;
    r.dataset.hsRail = 'ready';
    const wrap = document.createElement('div'); wrap.className = 'rail-wrap'; r.parentNode.insertBefore(wrap, r); wrap.appendChild(r);
    const mk = (cls, label, d, path) => {
      const b = document.createElement('button'); b.type = 'button'; b.className = 'rail-btn ' + cls; b.setAttribute('aria-label', label);
      b.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="${path}"/></svg>`;
      b.addEventListener('click', () => r.scrollBy({ left: d * r.clientWidth * 0.8, behavior: 'smooth' })); wrap.appendChild(b); return b;
    };
    const p = mk('rail-btn--prev', 'Scroll left', -1, 'm15 5-7 7 7 7'), n = mk('rail-btn--next', 'Scroll right', 1, 'm9 5 7 7-7 7');
    const upd = () => { p.disabled = r.scrollLeft < 8; n.disabled = r.scrollLeft + r.clientWidth >= r.scrollWidth - 8; };
    r.addEventListener('scroll', upd, { passive: true }); addEventListener('resize', upd); upd();
  });
}

function initAll(scope = document) {
  $$('[data-hs-product]', scope).forEach(initProduct);
  $$('[data-hs-recs-url]', scope).forEach(initRecs);
  $$('[data-hs-recent-wrap]', scope).forEach(initRecent);
  initRails(scope);
}
initAll();
/* Theme editor: re-run when a section is added or changed. */
document.addEventListener('shopify:section:load', e => initAll(e.target));
